import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { EditOutlined } from 'components/icons';
import { Link } from 'react-router';

import { Typography } from '../../../components/atoms';
import { InlineBulletList } from '../../../components/molecules';
import { Currency } from '../../../core/api/general/general.types';
import {
  CollectionRestaurantDetail,
  DeliveryRestaurantDetail,
  DeliveryType,
} from '../../../core/api/restaurant/restaurant.types';
import { DeliveryLocation } from '../../../core/api/user/user.types';
import { flagService } from '../../../core/services';
import { isPhoton } from '../../../weborder/Utilities';
import {
  getLocationDetails,
  getRestaurantNotes,
} from '../utils/locationHeaderUtils';

type Props = {
  currency: Currency;
  deliveryLocation: DeliveryLocation | null;
  languageCode: string;
  restaurantData: CollectionRestaurantDetail | DeliveryRestaurantDetail;
  translate: any;
};

const useStyles = makeStyles((theme: Theme) => ({
  locationHeader: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0, 2),
  },

  container: {
    alignItems: 'center',
    justifyContent: 'space-between',
    display: 'flex',
  },

  details: {
    marginRight: theme.spacing(5),
    display: 'flex',
    flexFlow: 'column',
    '& svg': {
      left: 0,
      position: 'absolute',
      top: 'calc(50% - 15px)',
    },

    '& .fd-text': {
      lineHeight: theme.typography.pxToRem(16),
    },

    '& .fd-text__body2:first-of-type': {
      paddingRight: theme.spacing(0.5),
    },
  },

  linkText: {
    marginLeft: theme.spacing(1),
  },
  edit: {
    display: 'flex',
    flexDirection: 'row',
    textDecorationStyle: `dashed`,
    textDecorationColor: theme.palette.primary.main,
  },

  orderType: {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(1),
    position: 'relative',
  },

  [theme.breakpoints.up('sm')]: {
    locationHeader: {
      padding: theme.spacing(0, 3),
    },
  },

  [theme.breakpoints.up('md')]: {
    locationHeader: {
      padding: 0,
    },

    details: {
      flexDirection: 'row',
      marginBottom: theme.spacing(1),

      '& .title': {
        marginRight: theme.spacing(1),
      },
    },

    orderType: {
      marginBottom: 0,
    },
  },
}));

export function LocationHeader(props: Props): JSX.Element | null {
  const {
    currency,
    deliveryLocation,
    languageCode,
    restaurantData,
    translate,
  } = props;

  const isNavigationUpateEnabled = flagService.isFlagOn('backNavigationUpdate');

  const classes = useStyles();

  const locationDetails = getLocationDetails(
    restaurantData,
    deliveryLocation,
    classes,
    'body2',
    translate,
  );

  const restaurantNotes = getRestaurantNotes(
    restaurantData,
    currency,
    languageCode,
    translate,
  );

  const locationHeaderStyles = clsx(
    classes.locationHeader,
    'fd-section__location-details',
  );

  const editLink =
    restaurantData.DeliveryType === DeliveryType.Delivery
      ? '/delivery'
      : '/collection';

  return (
    <div className={locationHeaderStyles}>
      <div className={classes.container}>
        <div className={classes.details}>
          <div className={classes.orderType}>
            {locationDetails.icon}

            <Typography>
              <em>{locationDetails.title}</em>
            </Typography>
          </div>

          {locationDetails.address || ''}
        </div>
        {isNavigationUpateEnabled && isPhoton() && (
          <Link to={editLink} className={classes.edit}>
            <EditOutlined size="xs" />
            <Typography className={classes.linkText} variant="body1">
              {translate('Menu_LocationHeader_Edit')}
            </Typography>
          </Link>
        )}
      </div>

      {!!restaurantNotes.length && <InlineBulletList notes={restaurantNotes} />}
    </div>
  );
}
